import { createSlice } from "@reduxjs/toolkit";
import { CardAccessType, CardEventAction, User } from "src/types/commonTypes";
import { StoreDispatch } from "../store";
import {
  CardDownloadRequest,
  CardSearchRequest,
  CreateCardRequest,
  UpdateCardRequest,
  cardEvent,
  cardSearch,
  createCard,
  downloadCard,
  updateCard
} from "src/network/graphql/cardService";
import {
  SearchUserRequest,
  deleteCardSetting,
  getCurrentCardSetting,
  getUserById,
  searchUser
} from "src/network/graphql/userService";
import { getAllTransaction } from "src/network/graphql/transactionService";
import { immediateToast } from "izitoast-react";
import { initialUserDetail } from "../userDetail/userDetailSlice";
import {
  SearchWalletRequest,
  getCardWallets,
  removeCardFromWallet,
  searchWallet
} from "src/network/graphql/walletService";
import { Wallet } from "../wallets/walletSlice";
import { showReportStatusPopup } from "src/components/UI/reportStatusPopup";
import { SearchGroupListRequest, getGroupList } from "src/network/graphql/groupService";
import { Group } from "../group/groupSlice";
import { EMPTY_STRING } from "src/components/constant/constant";
import { formatFullName } from "src/utils";
import { CardSettingType } from "../cardSetting/cardSettingSlice";
import { Transaction } from "../transactions/transactionSlice";

export type Card = {
  id: string;
  cardNumber: string;
  orderId: string;
  clientId: string;
  cardStatus: string;
  cardLimit: number;
  accessType: CardAccessType;
  user: User;
  cardHolderName: string;
  bsb: string;
  first_name: string;
  last_name: string;
  object_id: string;
  object_type: string;
  // CPC (card-as-wallet): the hidden GENERAL wallet backing this card (1:1). Drives card preferences.
  walletId?: string;
  address: {
    city: string;
    country: string;
    post_code: string;
    line_1: string;
    line_2: string;
    state: string;
  };
  organisationId?: number;
  created_at?: string;
  expires_at?: string;
};

export type CardDetailInsightsState = {
  loading: boolean;
  error: unknown | null;
  currentConnection: CardSettingType | null;
  lastTransaction: Transaction | null;
};

const initialCardDetailInsights: CardDetailInsightsState = {
  loading: false,
  error: null,
  currentConnection: null,
  lastTransaction: null
};
const initialCards: Card[] = [];
const initialUsers: User[] = [];

type orgList = {
  id: number;
  name: string;
  abn: string;
  address: string;
  phone: string;
  countryCode: string;
  website: string;
  facebook: string;
  linkedIn: string;
  instagram: string;
  referenceName: string;
  referenceRole: string;
  referenceCountryCode: string;
  referencePhone: string;
  referenceEmail: string;
  createdBy: string;
  createdOn: string;
  updatedBy: string;
  updatedOn: string;
};

const initialOrgList: orgList[] = [];
const initialWalletOptions: Wallet[] = [];

const initialGroups: Group[] = [];

const cardSlice = createSlice({
  initialState: {
    loading: false,
    updateCardLoading: false,
    updateStatusLoading: false,
    userLoading: false,
    userDataLoading: false,
    orgListLoading: false,
    orderCardLoading: false,
    cardPinLoading: false,
    downloadCardsLoading: false,
    syncOrderedCardLoading: false,
    cardConnectedWalletsLoading: false,
    removingCardConnection: false,
    error: null,
    cards: {
      list: initialCards,
      total: 0
    },
    users: {
      list: initialUsers,
      total: 0
    },
    userData: initialUserDetail,
    orgLists: {
      list: initialOrgList,
      total: 0
    },
    walletOptions: {
      list: initialWalletOptions
    },
    groups: {
      list: initialGroups,
      total: 0
    },
    groupLoading: false,
    cardDetailInsights: initialCardDetailInsights,
    cardConnectedWallets: initialWalletOptions,
    reopenCardDetailDrawerForCardId: null as string | null
  },
  name: "cards",
  reducers: {
    getAllCardsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    getAllCardsFail: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    getAllCardsSuccess: (state, action) => {
      state.loading = false;
      state.error = null;
      state.cards = action.payload;
    },
    updateCardStart: (state) => {
      state.updateCardLoading = true;
      state.error = null;
    },
    updateCardSuccess: (state) => {
      state.updateCardLoading = false;
    },
    updateCardFail: (state, action) => {
      state.updateCardLoading = false;
      state.error = action.payload;
    },
    updateCardStatusStart: (state) => {
      state.updateStatusLoading = true;
      state.error = null;
    },
    updateCardStatusSuccess: (state) => {
      state.updateStatusLoading = false;
    },
    updateCardStatusFail: (state, action) => {
      state.updateStatusLoading = false;
      state.error = action.payload;
    },
    fetchUsersStart: (state) => {
      state.userLoading = true;
    },
    fetchUsersSuccess: (state, action) => {
      state.userLoading = false;
      state.users.list = action.payload?.list ?? [];
      state.users.total = action.payload?.total ?? 0;
    },
    fetchUsersFail: (state, action) => {
      state.userLoading = false;
      state.users.list = [];
      state.users.total = 0;
      state.error = action.payload;
    },
    setUsersAction: (state, action) => {
      state.users.list = action.payload;
    },
    fetchUserDataStart: (state) => {
      state.userDataLoading = true;
    },
    fetchUserDataSuccess: (state, action) => {
      state.userDataLoading = false;
      state.userData = action.payload ?? initialUserDetail;
    },
    fetchUserDataFail: (state, action) => {
      state.userDataLoading = false;
      state.userData = initialUserDetail;
      state.error = action.payload;
    },
    fetchOrgListStart: (state) => {
      state.orgListLoading = true;
    },
    fetchOrgListSuccess: (state, action) => {
      state.orgListLoading = false;
      state.orgLists.list = action.payload?.list ?? [];
      state.orgLists.total = action.payload?.total ?? 0;
    },
    fetchOrgListFail: (state, action) => {
      state.orgListLoading = false;
      state.orgLists.list = [];
      state.orgLists.total = 0;
      state.error = action.payload;
    },
    setOrganisationAction: (state, action) => {
      state.orgLists.list = action.payload;
    },
    setWalletOptionsAction: (state, action) => {
      state.walletOptions.list = action.payload;
    },
    orderCardStart: (state) => {
      state.orderCardLoading = true;
    },
    orderCardSuccess: (state) => {
      state.orderCardLoading = false;
    },
    orderCardFail: (state, action) => {
      state.orderCardLoading = false;
      state.error = action.payload;
    },
    downloadCardsStart: (state) => {
      state.downloadCardsLoading = true;
    },
    downloadCardsSuccess: (state) => {
      state.downloadCardsLoading = false;
    },
    downloadCardsFail: (state, action) => {
      state.downloadCardsLoading = false;
      state.error = action.payload;
    },
    fetchGroupsStart: (state) => {
      state.groupLoading = true;
    },
    fetchGroupsSuccess: (state, action) => {
      state.groupLoading = false;
      state.groups.list = action.payload?.list ?? [];
      state.groups.total = action.payload?.total ?? 0;
    },
    fetchGroupsFail: (state, action) => {
      state.groupLoading = false;
      state.groups.list = [];
      state.groups.total = 0;
      state.error = action.payload;
    },
    setGroupsAction: (state, action) => {
      state.groups.list = action.payload;
    },
    fetchCardDetailInsightsStart: (state) => {
      state.cardDetailInsights.loading = true;
      state.cardDetailInsights.error = null;
    },
    fetchCardDetailInsightsSuccess: (state, action) => {
      state.cardDetailInsights.loading = false;
      state.cardDetailInsights.error = null;
      state.cardDetailInsights.currentConnection = action.payload.currentConnection;
      state.cardDetailInsights.lastTransaction = action.payload.lastTransaction;
    },
    fetchCardDetailInsightsFail: (state, action) => {
      state.cardDetailInsights.loading = false;
      state.cardDetailInsights.error = action.payload;
      state.cardDetailInsights.currentConnection = null;
      state.cardDetailInsights.lastTransaction = null;
    },
    clearCardDetailInsights: (state) => {
      state.cardDetailInsights = { ...initialCardDetailInsights };
    },
    setCurrentCardConnection: (state, action) => {
      state.cardDetailInsights.currentConnection = action.payload;
    },
    fetchCardConnectedWalletsStart: (state) => {
      state.cardConnectedWalletsLoading = true;
      state.error = null;
    },
    fetchCardConnectedWalletsSuccess: (state, action) => {
      state.cardConnectedWalletsLoading = false;
      state.cardConnectedWallets = action.payload ?? [];
      state.error = null;
    },
    fetchCardConnectedWalletsFail: (state, action) => {
      state.cardConnectedWalletsLoading = false;
      state.cardConnectedWallets = [];
      state.error = action.payload;
    },
    clearCardConnectedWallets: (state) => {
      state.cardConnectedWallets = [];
    },
    removeCardConnectionStart: (state) => {
      state.removingCardConnection = true;
      state.error = null;
    },
    removeCardConnectionSuccess: (state) => {
      state.removingCardConnection = false;
    },
    removeCardConnectionFail: (state, action) => {
      state.removingCardConnection = false;
      state.error = action.payload;
    },
    setReopenCardDetailDrawerForCardId: (state, action) => {
      state.reopenCardDetailDrawerForCardId = action.payload;
    }
  }
});

const {
  getAllCardsStart,
  getAllCardsSuccess,
  getAllCardsFail,
  updateCardStart,
  updateCardSuccess,
  updateCardFail,
  updateCardStatusStart,
  updateCardStatusSuccess,
  updateCardStatusFail,
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFail,
  fetchUserDataStart,
  fetchUserDataSuccess,
  fetchUserDataFail,
  orderCardStart,
  orderCardSuccess,
  orderCardFail,
  downloadCardsStart,
  downloadCardsSuccess,
  downloadCardsFail,
  fetchGroupsStart,
  fetchGroupsSuccess,
  fetchGroupsFail,
  fetchCardDetailInsightsStart,
  fetchCardDetailInsightsSuccess,
  fetchCardDetailInsightsFail,
  setCurrentCardConnection,
  fetchCardConnectedWalletsStart,
  fetchCardConnectedWalletsSuccess,
  fetchCardConnectedWalletsFail,
  removeCardConnectionStart,
  removeCardConnectionSuccess,
  removeCardConnectionFail
} = cardSlice.actions;

export const {
  setUsersAction,
  clearCardConnectedWallets,
  setOrganisationAction,
  setWalletOptionsAction,
  setGroupsAction,
  clearCardDetailInsights,
  setReopenCardDetailDrawerForCardId
} = cardSlice.actions;

export const getAllCardsAction = (data: CardSearchRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(getAllCardsStart());
    try {
      const response = await cardSearch(data);
      const structuredResponse = response.data.search_card.card_listing?.map(
        (card: any) =>
          ({
            id: card.card_id,
            bsb: card.bsb,
            cardStatus: card.card_status,
            accessType: card.card_type,
            cardNumber: card.ppan,
            orderId: card.order_id,
            clientId: card.clientId,
            cardLimit: card.card_limit,
            cardHolderName: card.holder_name,
            created_at: card.created_at,
            expires_at: card.expires_at,
            first_name: card.first_name,
            last_name: card.last_name,
            object_id: card.object_id,
            object_type: card.object_type,
            walletId: card.wallet_id,
            address: card.address,
            user: {
              id: card?.user_id,
              firstName: card?.user?.attributes?.first_name ?? "---",
              lastName: card?.user?.attributes?.last_name ?? " ",
              email: card?.user?.email ?? " ",
              address: card?.user?.attributes?.address ?? {},
              contact: card?.user?.phone ?? " "
            }
          }) as Card
      );

      dispatch(
        getAllCardsSuccess({
          list: structuredResponse,
          total: response.data.search_card.total_size
        })
      );
    } catch (error) {
      dispatch(getAllCardsFail(error));
    }
  };
};
/**
 * CPC cards search: resolve a user-name query to user ids first, then search
 * cards for those users. An empty user match short-circuits to an empty list.
 */
export const getAllCardsByUserNameAction = (userName: string, data: CardSearchRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(getAllCardsStart());
    try {
      const userResponse = await searchUser({
        user_filter: { full_name: userName },
        size: 50,
        from: 0
      });
      const userIds: string[] = (userResponse.data.search_user.user_listing ?? [])
        .map((user: { user_id?: string }) => user?.user_id)
        .filter((id: string | undefined): id is string => Boolean(id));

      if (userIds.length === 0) {
        dispatch(getAllCardsSuccess({ list: [], total: 0 }));
        return;
      }

      dispatch(
        getAllCardsAction({
          ...data,
          card_filter: { ...data.card_filter, user_ids: userIds }
        })
      );
    } catch (error) {
      dispatch(getAllCardsFail(error));
    }
  };
};

export const updateCardAction = (data: UpdateCardRequest, cb: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(updateCardStart());
    try {
      await updateCard(data);
      immediateToast("success", {
        message: `Card updated successfully`,
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(updateCardSuccess());
      if (cb) cb();
    } catch (error) {
      dispatch(updateCardFail(error));
    }
  };
};

export const updateCardStatusAction = (id: string, action: CardEventAction, cb: () => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(updateCardStatusStart());
    try {
      await cardEvent(id, action);
      immediateToast("success", {
        message: `Card ${action} successfully`,
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(updateCardStatusSuccess());
      if (cb) cb();
    } catch (error) {
      dispatch(updateCardStatusFail(error));
    }
  };
};


export const downloadCardsAction = (data: CardDownloadRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(downloadCardsStart());
    try {
      const response = await downloadCard(data);
      const reportId = response.data.download_cards.report_id;

      dispatch(downloadCardsSuccess(reportId));

      showReportStatusPopup({
        reportId,
        fileName: data?.report_name || "cards",
        onNavigateToReports: () => {
          window.location.href = "/reports";
        }
      });
    } catch (err) {
      dispatch(downloadCardsFail(err));
    }
  };
};

export const fetchUserListAction = (data: SearchUserRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchUsersStart());
    try {
      const response = await searchUser(data);

      const structuredResponse = response.data.search_user.user_listing.map((user: any) => ({
        id: user.user_id,
        firstName: user.attributes.first_name,
        lastName: user.attributes.last_name,
        email: user.email,
        role: user.current_organisation_role,
        status: user.is_active,
        userId: user.user_id
      }));

      dispatch(
        fetchUsersSuccess({
          list: structuredResponse,
          total: response.data.search_user.total_size
        })
      );
    } catch (error) {
      dispatch(fetchUsersFail(error));
    }
  };
};

export const fetchUserDataAction = (data: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchUserDataStart());
    try {
      const response = await getUserById({ user_id: data });
      const user = response.data.get_user;

      dispatch(
        fetchUserDataSuccess({
          user: {
            id: user.user_id,
            initial: user.attributes.initial,
            firstName: user.attributes.first_name,
            lastName: user.attributes.last_name,
            email: user.email,
            userName: user.username,
            gender: user.attributes.gender,
            dob: user.attributes.date_of_birth,
            role: user.roles,
            phoneNumber: user.phone,
            line1: user.attributes.address.line_1,
            line2: user.attributes.address.line_2,
            city: user.attributes.address.city,
            state: user.attributes.address.state,
            zipcode: user.attributes.address.post_code,
            country: user.attributes.address.country,
            active: user.is_active,
            kycStatus: user.security_status,
            guardianId: user.guardian_id,
            profileImage: user.attributes.profile_image,
            displayId: user.display_id
          }
        })
      );
    } catch (error) {
      dispatch(fetchUserDataFail(error));
    }
  };
};

export const fetchOrgListAction = (data: any) => {
  return data;
};
export const fetchWalletListAction = (data: SearchWalletRequest) => {
  return async (dispatch: StoreDispatch) => {
    try {
      const response = await searchWallet(data);

      const structuredResponse = response.data.search_wallet.items.map((wallet: any) => ({
        id: wallet.wallet_id,
        displayId: wallet.display_id,
        description: wallet.description,
        balance: wallet.balance,
        status: wallet.wallet_status,
        name: wallet.name,
        participant: {
          role: "PARTICIPANT",
          fullName: formatFullName(wallet.user?.attributes?.first_name, wallet.user?.attributes?.last_name),
          email: wallet?.user?.email
        }
      }));

      dispatch(setWalletOptionsAction(structuredResponse));
    } catch (error) {
      console.log(error);
    }
  };
};

export const orderCardAction = (data: CreateCardRequest, cb?: (_card_id: string) => void) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(orderCardStart());
    try {
      const response = (await createCard(data)) as {
        data?: {
          create_card: {
            card_id: string;
          };
        };
        errors?: Error[];
      };

      if (response.errors) {
        immediateToast("error", {
          message: response.errors[0].message,
          timeout: 3000,
          position: "topCenter"
        });
      } else {
        immediateToast("success", {
          message: "Card created successfully",
          timeout: 3000,
          position: "topCenter"
        });
        dispatch(orderCardSuccess());
        if (cb) cb(response.data?.create_card.card_id ?? EMPTY_STRING);
      }
    } catch (error) {
      dispatch(orderCardFail(error));
    }
  };
};


export const fetchGroupListAction = (data: SearchGroupListRequest) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchGroupsStart());
    try {
      const response = await getGroupList(data);

      const structuredResponse = response.data.search_group?.items;

      dispatch(
        fetchGroupsSuccess({
          list: structuredResponse,
          total: response.data.search_group.total_size
        })
      );
    } catch (error) {
      dispatch(fetchGroupsFail(error));
    }
  };
};

export const fetchCardConnectedWalletsAction = (cardId: string) => {
  return async (dispatch: StoreDispatch) => {
    dispatch(fetchCardConnectedWalletsStart());
    try {
      const response = await getCardWallets(cardId);
      const connectedWallets = response.data?.get_card_wallets?.map((wallet: any) => ({
        id: wallet.wallet_id,
        displayId: wallet.display_id,
        description: wallet.description,
        status: wallet.wallet_status,
        name: wallet.name,
        walletType: wallet.wallet_type,
        participant: wallet.user
          ? {
              role: "PARTICIPANT",
              fullName: formatFullName(wallet.user?.attributes?.first_name, wallet.user?.attributes?.last_name),
              email: wallet?.user?.email,
              id: wallet?.user?.user_id
            }
          : null,
        group: wallet?.group
      }));

      dispatch(fetchCardConnectedWalletsSuccess(connectedWallets ?? []));
    } catch (error) {
      dispatch(fetchCardConnectedWalletsFail(error));
    }
  };
};

export const removeCardConnectedWalletAction = (walletId: string, cardId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    try {
      await removeCardFromWallet(walletId, cardId, false);

      immediateToast("success", {
        message: "Card removed from card",
        timeout: 3000,
        position: "topCenter"
      });
      dispatch(fetchCardConnectedWalletsAction(cardId));
      if (cb) cb();
    } catch {
      immediateToast("error", {
        message: "Failed to remove card from card",
        timeout: 3000,
        position: "topCenter"
      });
    }
  };
};

type WalletMapEntry = NonNullable<CardSettingType["wallet"]>;
type UserMapEntry = NonNullable<CardSettingType["user"]>;

function getStringId(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  const s = String(value).trim();

  return s.length ? s : null;
}

function extractPayerUserId(ownerId: string, data: Record<string, unknown> | null): string | null {
  if (data) {
    const keys = ["payer_user_id", "user_id", "transaction_user_id", "payer_id", "participant_user_id"];

    for (const k of keys) {
      const id = getStringId(data[k]);

      if (id) return id;
    }

    const userObj = data.user;

    if (userObj && typeof userObj === "object" && userObj !== null) {
      const id = getStringId((userObj as Record<string, unknown>).user_id);

      if (id) return id;
    }
  }

  return getStringId(ownerId);
}

function getNonEmptyString(value: unknown): string {
  if (value === undefined || value === null) return "";
  const s = String(value).trim();

  return s;
}

function extractWalletNameFromData(data: Record<string, unknown> | null): string {
  if (!data) return "";

  const walletObj = data.wallet;

  if (walletObj && typeof walletObj === "object" && walletObj !== null) {
    const n = getNonEmptyString((walletObj as Record<string, unknown>).name);

    if (n) return n;
  }

  const dest = data.destination_wallet;

  if (dest && typeof dest === "object" && dest !== null) {
    const n = getNonEmptyString((dest as Record<string, unknown>).name);

    if (n) return n;
  }

  return getNonEmptyString(data.wallet_name);
}

function extractWalletIdFromData(data: Record<string, unknown> | null): string | null {
  if (!data) return null;

  const walletObj = data.wallet;

  if (walletObj && typeof walletObj === "object" && walletObj !== null) {
    const w = walletObj as Record<string, unknown>;
    const fromNested = getStringId(w.wallet_id) || getStringId(w.id);

    if (fromNested) return fromNested;
  }

  const flatKeys = ["destination_wallet_id", "wallet_id", "source_wallet_id"];

  for (const k of flatKeys) {
    const id = getStringId(data[k]);

    if (id) return id;
  }

  const dest = data.destination_wallet;

  if (dest && typeof dest === "object" && dest !== null) {
    const d = dest as Record<string, unknown>;
    const id = getStringId(d.wallet_id) || getStringId(d.id);

    if (id) return id;
  }

  return null;
}

function enrichLastTransaction(
  tx: Transaction,
  walletMap: Map<string, WalletMapEntry>,
  userMap: Map<string, UserMapEntry>
): Transaction {
  const loose = tx.data as unknown as Record<string, unknown>;
  const payerUserId = extractPayerUserId(tx.ownerId, loose);
  const walletId = extractWalletIdFromData(loose);
  const userEntry = payerUserId ? userMap.get(payerUserId) : undefined;
  const walletEntry = walletId ? walletMap.get(walletId) : undefined;
  const nameFromPayload = extractWalletNameFromData(loose);

  const u = tx.data.user;
  let nextUser: Transaction["data"]["user"] = {
    user_id: u?.user_id,
    email: u?.email,
    attributes: {
      first_name: u?.attributes?.first_name,
      last_name: u?.attributes?.last_name
    }
  };

  if (userEntry && payerUserId) {
    nextUser = {
      user_id: payerUserId,
      email: userEntry.email,
      attributes: {
        first_name: userEntry.first_name,
        last_name: userEntry.last_name
      }
    };
  } else if (payerUserId) {
    nextUser = {
      ...nextUser,
      user_id: payerUserId
    };
  }

  const w = tx.data.wallet;
  const baseWallet: Transaction["data"]["wallet"] = {
    created_at: w?.created_at,
    description: w?.description,
    name: w?.name,
    updated_at: w?.updated_at,
    wallet_type: w?.wallet_type,
    balance: w?.balance ?? 0,
    display_id: w?.display_id,
    wallet_id: w?.wallet_id,
    object_type: w?.object_type,
    object_id: w?.object_id
  };

  const effectiveWalletId = walletId || baseWallet.wallet_id;
  const resolvedWalletName = walletEntry?.name?.trim() || nameFromPayload || baseWallet.name.trim() || "";

  return {
    ...tx,
    data: {
      ...tx.data,
      user: nextUser,
      wallet: {
        ...baseWallet,
        wallet_id: effectiveWalletId,
        name: resolvedWalletName || baseWallet.name,
        wallet_type: walletEntry?.wallet_type ?? baseWallet.wallet_type
      }
    }
  };
}

const mapCurrentCardSetting = (raw: any): CardSettingType | null => {
  if (!raw) return null;

  return {
    card_setting_id: raw.card_setting_id,
    card_setting_status: raw.card_setting_status,
    status: raw.status,
    ppan: raw.ppan,
    card_id: raw.card_id,
    wallet_id: raw.wallet_id,
    expires_at: raw.expires_at,
    sub_type: raw.sub_type,
    user_id: raw.user_id,
    updated_at: raw.updated_at,
    wallet: raw.wallet
      ? {
          name: raw.wallet.name,
          description: EMPTY_STRING,
          wallet_type: EMPTY_STRING,
          balance: raw.wallet.balance ?? null
        }
      : null,
    user: raw.user
      ? {
          first_name: raw.user.attributes?.first_name ?? EMPTY_STRING,
          last_name: raw.user.attributes?.last_name ?? EMPTY_STRING,
          email: raw.user.email ?? EMPTY_STRING,
          role: EMPTY_STRING
        }
      : null
  };
};

export const fetchCardDetailInsightsAction = (cardId: string, ppan: string) => {
  return async (dispatch: StoreDispatch) => {
    const trimmed = cardId?.trim();
    const ppanTrimmed = ppan?.trim();

    if (!trimmed) {
      dispatch(clearCardDetailInsights());

      return;
    }

    dispatch(fetchCardDetailInsightsStart());

    try {
      const activityRequest = ppanTrimmed
        ? getAllTransaction({
            size: 1,
            from: 0,
            filter: { ppans: [ppanTrimmed] }
          })
        : Promise.resolve({ data: { search_activity: { activity_listing: [] as unknown[] } } });

      const [currentSettingResp, activityResp] = await Promise.all([getCurrentCardSetting(trimmed), activityRequest]);

      const currentConnection = mapCurrentCardSetting(currentSettingResp.data?.get_current_card_setting ?? null);

      const activityListing = activityResp.data?.search_activity?.activity_listing ?? [];
      const lastTxBase = activityListing[0]
        ? ({
            id: activityListing[0].activity_id,
            name: activityListing[0].activity_name,
            endDate: activityListing[0].end_timestamp,
            startDate: activityListing[0].created_at,
            ownerId: activityListing[0].owner_id,
            activityStatus: activityListing[0].activity_status,
            receipt_available: activityListing[0].receipt_available,
            approval_number: activityListing[0].approval_number,
            data: JSON.parse(activityListing[0]?.data || "{}"),
            xero_details: activityListing[0]?.xero_details,
            verified: activityListing[0]?.attributes?.verified,
            tagged_users: activityListing[0]?.attributes?.tagged_users,
            expense_id: activityListing[0]?.attributes?.expense_id,
            accounting_status: activityListing[0]?.attributes?.accounting_status
          } as Transaction)
        : null;

      let lastTransaction: Transaction | null = null;

      if (lastTxBase) {
        const looseData = lastTxBase.data as unknown as Record<string, unknown>;
        const walletId = extractWalletIdFromData(looseData);
        const userId = extractPayerUserId(lastTxBase.ownerId, looseData);

        const [walletResponse, userResponse] = await Promise.all([
          walletId
            ? searchWallet({ walletFilter: { wallet_ids: [walletId] }, size: 1, from: 0 })
            : Promise.resolve({ data: { search_wallet: { items: [] } } }),
          userId
            ? searchUser({ user_filter: { user_ids: [userId] }, size: 1, from: 0 })
            : Promise.resolve({ data: { search_user: { user_listing: [] } } })
        ]);

        const walletMap = new Map<string, WalletMapEntry>(
          walletResponse.data.search_wallet.items.map((wallet: any) => [
            wallet.wallet_id,
            {
              name: wallet.name,
              description: wallet.description,
              wallet_type: wallet.wallet_type
            }
          ])
        );

        const userMap = new Map<string, UserMapEntry>(
          userResponse.data.search_user.user_listing.map((user: any) => [
            user.user_id,
            {
              first_name: user.attributes.first_name,
              last_name: user.attributes.last_name,
              email: user.email,
              role: user.current_organisation_role
            }
          ])
        );

        lastTransaction = enrichLastTransaction(lastTxBase, walletMap, userMap);
      }

      dispatch(
        fetchCardDetailInsightsSuccess({
          currentConnection,
          lastTransaction
        })
      );
    } catch (error) {
      dispatch(fetchCardDetailInsightsFail(error));
    }
  };
};

export const removeCardConnectionAction = (cardId: string, walletId: string, cb?: () => void) => {
  return async (dispatch: StoreDispatch) => {
    if (!cardId?.trim() || !walletId?.trim()) return;

    dispatch(removeCardConnectionStart());
    try {
      await deleteCardSetting(cardId, walletId);

      immediateToast("success", {
        message: "Card connection removed successfully",
        timeout: 3000,
        position: "topCenter"
      });

      const refreshResp = await getCurrentCardSetting(cardId);

      dispatch(setCurrentCardConnection(mapCurrentCardSetting(refreshResp.data?.get_current_card_setting ?? null)));
      dispatch(removeCardConnectionSuccess());

      cb?.();
    } catch (error) {
      dispatch(removeCardConnectionFail(error));
    }
  };
};

export default cardSlice.reducer;
